import { signIn, SignInResponse } from "next-auth/react";
import { useRouter } from "next/router";
import { Routes } from "../../../../routes";
import React, { useState } from "react";
import { useUser } from "~/modules/user/hooks/useUser";
import * as Analytics from "../../analytics";
import { LoadingBoundary } from "~/components/LoadingBoundary";
import { getEmailVerifyRedirectUrl } from "../../util";
import { isSignInResponseOk } from "../../util/isAuthResponseOk";
import { AuthPageLayout } from "../../components/AuthPageLayout";
import { OAuthButton } from "../../components/OAuthButton";
import { EmailForm } from "../../components/EmailForm";
import {
    AuthFormCard,
    AuthFormCardSeparator,
} from "../../components/AuthFormCard";
import { LoggedInCard } from "../../components/LoggedInCard";

// // // //

function RegisterCard() {
    const router = useRouter();

    // Track loading state of email form submission
    const [loadingAction, setLoadingAction] = useState<
        "email" | "google" | null
    >(null);

    // Handle callbackUrl query parameter
    const callbackUrl = Array.isArray(router.query.callbackUrl)
        ? router.query.callbackUrl[0]
        : router.query.callbackUrl;

    // Callback function for email form submission + redirects
    async function submitEmailForm(email: string) {
        // Track email login event
        Analytics.trackEmailRegister({ email });

        // Set loading state
        setLoadingAction("email");

        // Submit email form
        const response: SignInResponse | undefined = await signIn("email", {
            email,
            redirect: false,
        });

        // Handle error if there is no response
        // (it could potentially be `undefined`)
        if (!response) {
            Analytics.trackEmailRegisterResponseUndefined({ email });
            return;
        }

        // Check if response is ok
        const isResponseOk = isSignInResponseOk(response);

        // Handle OTP email error
        if (!isResponseOk) {
            // Capture error event
            Analytics.trackEmailRegisterResponseOkFalse({ email });

            // Redirect to login page with error
            router.replace(Routes.authError);
            return;
        }

        // Email sent successfully -> redirect to verify page
        Analytics.trackEmailRegisterSuccess({ email });
        router.replace(getEmailVerifyRedirectUrl({ email }));
    }

    const disabled = loadingAction !== null;

    return (
        <AuthFormCard variant="register">
            <EmailForm
                disabled={disabled}
                onSubmit={submitEmailForm}
                showLoading={loadingAction === "email"}
            />

            <AuthFormCardSeparator />

            <OAuthButton
                loading={loadingAction === "google"}
                disabled={disabled}
                provider="google"
                variant="register"
                onClick={() => {
                    Analytics.trackGoogleRegister();
                    signIn("google", {
                        redirect: true,
                        callbackUrl,
                    });
                }}
            />
        </AuthFormCard>
    );
}

export function RegisterPage() {
    const { user, isLoggedIn, loading } = useUser();

    return (
        <LoadingBoundary className="h-full" loading={loading}>
            <AuthPageLayout>
                {!isLoggedIn && <RegisterCard />}
                {isLoggedIn && (
                    <LoggedInCard email={user.email || ""} variant="register" />
                )}
            </AuthPageLayout>
        </LoadingBoundary>
    );
}
